
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import type { Database } from '@/lib/supabase';
import { OldGoldTrade } from '../types';

type InventoryItem = Database['public']['Tables']['inventory']['Row'];
type InventoryInsert = Database['public']['Tables']['inventory']['Insert'];
type InventoryUpdate = Database['public']['Tables']['inventory']['Update'];

export const useInventory = () => {
  const queryClient = useQueryClient();

  const useInventoryItems = () => {
    return useQuery({
      queryKey: ['inventory'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('inventory')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        return data;
      },
    });
  };

  const useCreateInventoryItem = () => {
    return useMutation({
      mutationFn: async (item: InventoryInsert) => {
        const { data, error } = await supabase
          .from('inventory')
          .insert(item)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });
  };

  const useUpdateInventoryItem = () => {
    return useMutation({
      mutationFn: async ({ id, ...updates }: InventoryUpdate & { id: string }) => {
        const { data, error } = await supabase
          .from('inventory')
          .update(updates)
          .eq('id', id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });
  };

  // Transfer stock functionality
  const useTransferStock = () => {
    return useMutation({
      mutationFn: async (transferData: any) => {
        const response = await api.post('/inventory/transfer', transferData);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });
  };

  // Old Gold functionality with proper data handling
  const oldGold = useQuery({
    queryKey: ['oldGold'],
    queryFn: async () => {
      const stored = localStorage.getItem('oldGoldTrades');
      const trades = stored ? JSON.parse(stored) : [];
      
      // Return mock data if no stored data
      if (trades.length === 0) {
        const mockTrades = [
          {
            id: '1',
            customer: 'Rajesh Kumar',
            phone: '+91-9876543210',
            itemType: 'Gold Ring',
            weight: 15.5,
            purity: '22K',
            rate: 6250,
            grossValue: 96875,
            deduction: 2000,
            finalValue: 94875,
            date: new Date().toISOString()
          },
          {
            id: '2',
            customer: 'Priya Sharma',
            phone: '+91-9876543211',
            itemType: 'Gold Chain',
            weight: 25.2,
            purity: '18K',
            rate: 4850,
            grossValue: 122220,
            deduction: 3000,
            finalValue: 119220,
            date: new Date().toISOString()
          }
        ];
        localStorage.setItem('oldGoldTrades', JSON.stringify(mockTrades));
        return mockTrades;
      }
      
      return trades;
    },
  });

  const addOldGold = useMutation({
    mutationFn: async (trade: any) => {
      const stored = localStorage.getItem('oldGoldTrades');
      const trades = stored ? JSON.parse(stored) : [];
      const newTrade = {
        ...trade,
        id: Date.now().toString(),
        date: new Date().toISOString()
      };
      const newTrades = [...trades, newTrade];
      localStorage.setItem('oldGoldTrades', JSON.stringify(newTrades));
      return newTrade;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oldGold'] });
    },
  });

  // Vendor management
  const useVendors = () => {
    return useQuery({
      queryKey: ['vendors'],
      queryFn: async () => {
        const response = await api.get('/vendor/list');
        return response.data;
      },
    });
  };

  return {
    useInventoryItems,
    useCreateInventoryItem,
    useUpdateInventoryItem,
    useTransferStock,
    oldGold,
    addOldGold,
    useVendors
  };
};

export type { InventoryItem };